/**
 * The SSE wire, both halves: a pure frame parser over any
 * `ReadableStream<Uint8Array>` (`parseTurnEvents`, independent of `fetch` and
 * so directly testable against a canned byte stream), and the authenticated
 * request that opens one against `api`'s real route (`openTurnStream`).
 *
 * `ApiClient` (`../../../lib/api.ts`) has no streaming method and is outside
 * this task's files, so this builds its own `fetch` call the same way that
 * client does: a fresh Firebase token per request, `loadConfig()` for the
 * base URL.
 */

import { z } from 'zod';

import type { FetchLike, TokenSource } from '../../../lib/api';
import { ApiError } from '../../../lib/api';
import { loadConfig, type Config } from '../../../lib/config';
import { firebaseIdToken } from '../../session/token';
import { EVENT_DATA_SCHEMAS, isTurnEventKind, type TurnEvent, type TurnRequest } from './schemas';

interface RawFrame {
  event: string;
  data: string;
}

/** One blank-line-terminated SSE frame at a time, decoded incrementally so a
 *  frame split across two network chunks still parses. A `:`-prefixed line
 *  is a comment (`sse-starlette`'s own keep-alive) and carries no data.
 *
 *  The SSE spec ends a line with CRLF, CR or LF, and `sse-starlette` sends
 *  CRLF, so every ending is folded to LF before a boundary is looked for. A
 *  chunk ending on CR holds it back: its LF may be the next chunk's first
 *  byte, and folding the two apart would read one line ending as two. */
async function* rawFrames(stream: ReadableStream<Uint8Array>): AsyncGenerator<RawFrame> {
  const reader = stream.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let pending = '';
  try {
    for (;;) {
      const { done, value } = await reader.read();
      const text = pending + (done ? decoder.decode() : decoder.decode(value, { stream: true }));
      pending = !done && text.endsWith('\r') ? '\r' : '';
      buffer += (pending ? text.slice(0, -1) : text).replace(/\r\n?/g, '\n');
      let boundary = buffer.indexOf('\n\n');
      while (boundary !== -1) {
        const frame = parseFrame(buffer.slice(0, boundary));
        if (frame !== null) yield frame;
        buffer = buffer.slice(boundary + 2);
        boundary = buffer.indexOf('\n\n');
      }
      if (done) break;
    }
  } finally {
    reader.releaseLock();
  }
}

function parseFrame(raw: string): RawFrame | null {
  let event = 'message';
  const dataLines: string[] = [];
  for (const line of raw.split('\n')) {
    if (line.startsWith(':')) continue;
    if (line.startsWith('event:')) event = line.slice('event:'.length).trim();
    else if (line.startsWith('data:')) dataLines.push(line.slice('data:'.length).trim());
  }
  return dataLines.length === 0 ? null : { event, data: dataLines.join('\n') };
}

function frameData(raw: string): unknown {
  try {
    return JSON.parse(raw);
  } catch {
    return undefined;
  }
}

/** An event kind this client does not know, or a frame it cannot read, is
 *  skipped rather than thrown — a server ahead of this client's contract does
 *  not break an open turn. Only a stream that ends without `done`, `refusal`
 *  or `error` reads as cut. */
export async function* parseTurnEvents(
  stream: ReadableStream<Uint8Array>,
): AsyncGenerator<TurnEvent> {
  for await (const frame of rawFrames(stream)) {
    if (!isTurnEventKind(frame.event)) continue;
    const parsed = EVENT_DATA_SCHEMAS[frame.event].safeParse(frameData(frame.data));
    if (parsed.success) yield { kind: frame.event, data: parsed.data } as TurnEvent;
  }
}

/** `api` answers a turn it cannot serve with a 503 whose `detail` is a
 *  sentence for the reader, rendered like an `error` event from an open stream. */
async function unavailableMessage(response: Response): Promise<string | null> {
  try {
    const body: unknown = await response.json();
    const parsed = z.object({ detail: z.string() }).safeParse(body);
    return parsed.success ? parsed.data.detail : null;
  } catch {
    return null;
  }
}

async function* singleEvent(event: TurnEvent): AsyncGenerator<TurnEvent> {
  yield event;
}

export async function openTurnStream(
  campaignId: string,
  turn: TurnRequest,
  deps: {
    config?: Config;
    getToken?: TokenSource;
    fetchImpl?: FetchLike;
    signal?: AbortSignal;
  } = {},
): Promise<AsyncGenerator<TurnEvent>> {
  const config = deps.config ?? loadConfig();
  const getToken = deps.getToken ?? firebaseIdToken;
  // Not `= fetch`, per `[real-browser]`: a bound reference keeps `fetch`'s
  // required `window` receiver instead of this module's scope.
  const fetchImpl: FetchLike = deps.fetchImpl ?? ((...args) => fetch(...args));

  const token = await getToken();
  if (!token) throw new ApiError(401, 'not signed in');

  const path = `/chat/campaigns/${campaignId}`;
  const response = await fetchImpl(`${config.apiBaseUrl}${path}`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
    },
    body: JSON.stringify(turn),
    signal: deps.signal,
  });

  if (response.status === 503) {
    const message = await unavailableMessage(response);
    if (message !== null) return singleEvent({ kind: 'error', data: { message } });
  }
  if (!response.ok) throw new ApiError(response.status, `POST ${path} failed`);
  if (response.body === null) throw new ApiError(response.status, `POST ${path} returned no body`);

  return parseTurnEvents(response.body);
}
